import {
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CalendarIcon } from '@heroicons/react/24/solid';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { InputProps } from 'react-daisyui';
import { DayPicker, type DateRange, type Matcher } from 'react-day-picker';
import { useMediaQuery, useOnClickOutside } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/classNames.ts';
import dayjs, { dateRange } from '$/utils/dayjs';

const VALUE_FORMAT = 'YYYY-MM-DD';

export type DateRangeValue = {
  from?: string;
  to?: string;
};

type DateRangePickerInputProps = {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  minDate: Date;
  maxRangeDays: number;
  label?: string;
  placeholder?: string;
  error?: string;
  wrapperClassName?: string;
} & Pick<InputProps, 'size' | 'color' | 'bordered'>;

const parseValue = (value?: string) =>
  value ? dayjs(value, VALUE_FORMAT).toDate() : undefined;

const formatValue = (date: Date) => dayjs(date).format(VALUE_FORMAT);

function DateRangePickerInput({
  value,
  onChange,
  minDate,
  maxRangeDays,
  label,
  placeholder,
  error,
  wrapperClassName,
  size = 'md',
  color,
  bordered = true,
}: DateRangePickerInputProps) {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const showTwoMonths = useMediaQuery('(min-width: 768px)');

  const fromDate = useMemo(() => parseValue(value.from), [value.from]);
  const toDate = useMemo(() => parseValue(value.to), [value.to]);

  const isSelectingReturn = !!fromDate && !toDate;
  const hasValue = !!fromDate || !!toDate;

  const selected = useMemo<DateRange | undefined>(
    () => (fromDate ? { from: fromDate, to: toDate } : undefined),
    [fromDate, toDate]
  );

  const disabled = useMemo<Matcher[]>(() => {
    const matchers: Matcher[] = [{ before: minDate }];

    if (isSelectingReturn && fromDate) {
      matchers.push({ before: fromDate });
      matchers.push({
        after: dayjs(fromDate).add(maxRangeDays, 'day').toDate(),
      });
    }

    return matchers;
  }, [isSelectingReturn, fromDate, minDate, maxRangeDays]);

  const nights = useMemo(
    () =>
      fromDate && toDate ? dayjs(toDate).diff(dayjs(fromDate), 'day') : 0,
    [fromDate, toDate]
  );

  useOnClickOutside(wrapperRef, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  const handleSelect = (_range: DateRange | undefined, selectedDay: Date) => {
    if (!fromDate || toDate) {
      onChange({ from: formatValue(selectedDay), to: undefined });
      return;
    }

    if (dayjs(selectedDay).isSame(fromDate, 'day')) {
      onChange({ from: undefined, to: undefined });
      return;
    }

    onChange({ from: formatValue(fromDate), to: formatValue(selectedDay) });
    setIsOpen(false);
  };

  const handleClear = (
    event: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>
  ) => {
    event.stopPropagation();
    onChange({ from: undefined, to: undefined });
  };

  const summary = () => {
    if (fromDate && toDate) return dateRange(value.from!, value.to!);
    if (fromDate) return dayjs(fromDate).format('D MMM');
    return placeholder;
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'form-control relative w-full max-w-full',
        wrapperClassName
      )}
    >
      {label ? (
        <label className="label">
          <span className="label-text">{label}</span>
        </label>
      ) : null}

      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((previous) => !previous)}
        className={cn(
          'input flex max-w-full cursor-pointer items-center justify-between gap-2 text-left',
          {
            'input-ghost': color === 'ghost',
            'input-primary': color === 'primary',
            'input-secondary': color === 'secondary',
            'input-accent': color === 'accent',
            'input-info': color === 'info',
            'input-success': color === 'success',
            'input-warning': color === 'warning',
            'input-error': error || color === 'error',
            'input-xs': size === 'xs',
            'input-sm': size === 'sm',
            'input-md': size === 'md',
            'input-lg': size === 'lg',
            'input-bordered': bordered,
            'ring-2 ring-primary ring-offset-0': isOpen,
          }
        )}
      >
        <CalendarIcon className="w-4 shrink-0 text-primary" />
        <span
          className={cn('grow truncate text-base', {
            'text-text-secondary': !hasValue,
          })}
        >
          {summary()}
        </span>

        {nights > 0 ? (
          <span className="badge badge-sm shrink-0 border-none bg-primary bg-opacity-15 font-medium text-primary">
            {t('tripPage.nights', { count: nights })}
          </span>
        ) : null}

        {hasValue ? (
          <span
            role="button"
            tabIndex={0}
            aria-label={t('tripCreateForm.date.clearDates')}
            className="shrink-0 opacity-50 transition-opacity hover:opacity-100"
            onClick={handleClear}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                handleClear(event);
              }
            }}
          >
            <XMarkIcon className="w-4" />
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <div
          role="dialog"
          aria-label={t('tripCreateForm.date.calendarLabel')}
          className="absolute right-0 top-[calc(100%+8px)] z-20 w-max max-w-[calc(100vw-2rem)] overflow-x-auto rounded-box border border-base-300 bg-base-100 p-4 shadow-xl"
        >
          <DayPicker
            mode="range"
            min={1}
            max={maxRangeDays}
            selected={selected}
            onSelect={handleSelect}
            disabled={disabled}
            startMonth={minDate}
            defaultMonth={fromDate ?? minDate}
            numberOfMonths={showTwoMonths ? 2 : 1}
            showOutsideDays={false}
          />

          <p className="mt-1 border-t border-base-300 pt-3 text-center text-sm text-text-secondary">
            {isSelectingReturn
              ? t('tripCreateForm.date.pickReturn')
              : t('tripCreateForm.date.pickArrival', {
                  count: maxRangeDays,
                })}
          </p>
        </div>
      ) : null}

      {error ? (
        <label className="label">
          <span className="label-text-alt text-error">{error}</span>
        </label>
      ) : null}
    </div>
  );
}

export default DateRangePickerInput;